import { useReactiveVar } from "@apollo/client";
import { Modal, Loading } from "@dmm-com/rocket-web-ui-library";
import { memo, useEffect, useMemo } from "react";
import { PurchaseMainContent } from "./purchase-main-content";
import { PurchasePremiumAppealBanner } from "./purchase-premium-appeal-banner";
import { PurchaseTitleContent } from "./purchase-title-content";
import * as Styled from "./style";
import { useRouter } from "next/router";
import { isOpenPurchaseModalVar, isWakuwakuContentVar } from "~/components/stores/detail-product/store";
import { EventWakuwakuContent } from "~/components/templates/common/purchase-modal/event-wakuwaku-content";
import { PurchasePointBlock } from "~/components/templates/common/purchase-modal/purchase-main-content/point-block";
import { useMatchMedia } from "~/hooks/use-match-media";
import { usePurchaseModalActions } from "~/hooks/use-purchase-modal-actions";
import { useQueryString } from "~/hooks/use-query-string";

import { G_VideoSeasonType, useFetchVideoContentLazyQuery, useGetVideoPurchaseModalDataLazyQuery } from "~/lib/graphql";
import { useAuth } from "~/provider/auth";
import { Z_INDEX } from "~/styles/z-index";
import { getPlayDevice } from "~/util/get-device";

/** 購入モーダルコンポーネント */
export const PurchaseModal = memo(() => {
  const { isLoggedIn } = useAuth();

  const [videoContentExec, { loading: isPurchaseModalLoading, data: purchaseModalData }] =
    useFetchVideoContentLazyQuery();

  const [videoPurchaseModalExec, { loading: isVideoPurchaseModalLoading, data: videoPurchaseModalData }] =
    useGetVideoPurchaseModalDataLazyQuery();

  const { isReady } = useRouter();
  const { purchaseContent } = useQueryString();
  const isOpenPurchaseModal = useReactiveVar(isOpenPurchaseModalVar);
  const isWakuwakuContent = useReactiveVar(isWakuwakuContentVar);

  const { openPurchaseModal, closeWakuwakuModalContent, initializePurchaseModal, pushToBackPurchaseModal } =
    usePurchaseModalActions();

  const { isSpSize } = useMatchMedia();

  const modalSize = useMemo(() => (!isSpSize ? "middle" : "fullScreen"), [isSpSize]);

  useEffect(() => {
    if (!isReady || purchaseContent === undefined) return;

    (async () => {
      const { data: contentData } = await videoContentExec({
        variables: {
          id: purchaseContent,
          playDevice: getPlayDevice(),
          isLoggedIn,
        },
      });

      if (!contentData) return;

      void videoPurchaseModalExec({
        variables: {
          seasonId: contentData.videoContent?.ppvProducts[0]?.seasonId ?? "",
        },
      });
    })();

    openPurchaseModal();

    return () => {
      initializePurchaseModal();
    };
  }, [isReady, purchaseContent]);

  if (!isOpenPurchaseModal) return null;

  if (isPurchaseModalLoading || !videoPurchaseModalData || isVideoPurchaseModalLoading || !purchaseModalData) {
    return <Loading size={!isSpSize ? 120 : 80} />;
  }

  const { titleName, seasonName, seasonType, campaign } = videoPurchaseModalData.video ?? {};
  const { isWakuwari, svodProduct, ppvProducts } = purchaseModalData.videoContent ?? {};

  const isLimitedPremium = campaign?.isLimitedPremium ?? false;
  return (
    <Modal
      basisZIndex={Z_INDEX.MODAL_IN_MODAL}
      size={modalSize}
      isOpen={isOpenPurchaseModal}
      isLoadingAPI={isPurchaseModalLoading || isWakuwakuContent}
      isShowBodyCloseButton
      onCloseModal={!isWakuwakuContent ? pushToBackPurchaseModal : closeWakuwakuModalContent}
    >
      <Styled.DivRootGridContainer>
        {seasonType === G_VideoSeasonType.Stage ? (
          !isWakuwakuContent ? (
            <>
              <PurchaseTitleContent
                titleName={titleName}
                seasonName={seasonName}
                isWakuwari={isWakuwari}
                campaign={campaign}
              />
              <PurchasePointBlock />
              <PurchasePremiumAppealBanner
                isLimitedPremium={isLimitedPremium}
                svodProduct={svodProduct}
                ppvProducts={ppvProducts}
              />
              <PurchaseMainContent seasonType={seasonType} isWakuwari={isWakuwari} ppvProducts={ppvProducts} />
            </>
          ) : (
            <EventWakuwakuContent />
          )
        ) : (
          <>
            <PurchaseTitleContent
              titleName={titleName}
              seasonName={seasonName}
              isWakuwari={isWakuwari}
              campaign={campaign}
            />
            <PurchasePointBlock />
            <PurchasePremiumAppealBanner
              isLimitedPremium={isLimitedPremium}
              svodProduct={svodProduct}
              ppvProducts={ppvProducts}
            />

            <PurchaseMainContent seasonType={seasonType} isWakuwari={isWakuwari} ppvProducts={ppvProducts} />
          </>
        )}
      </Styled.DivRootGridContainer>
    </Modal>
  );
});

PurchaseModal.displayName = "PurchaseModal";
